/**
 * `useMe()` — the signed-in person's `/api/v1/me`, **one request shared by
 * every part of the page** (ADO 2545).
 *
 * The sidebar, Home, the route guards and each page all ask for the profile.
 * They used to fetch it separately — three requests at sign-in, and again on
 * every page change, each waiting for its own token — so one answer is now
 * kept for the signed-in account and handed to every caller:
 *
 * - **One request at a time.** Callers that mount together share the one in
 *   flight.
 * - **Fresh enough.** A caller that mounts when the answer is older than a
 *   minute refreshes it in the background, so a role or grant an
 *   Administrator changed shows up within a minute of moving around the app,
 *   as before; the page keeps the answer it has while the refresh runs.
 * - **Never across people.** The answer is keyed on the signed-in account and
 *   dropped when the account changes or signs out. It lives in this tab's
 *   memory only — nothing is written to storage.
 * - `reload()` fetches again for everyone; `update(next)` replaces it for
 *   everyone.
 *
 * The backend re-checks every request: this decides only what the page shows
 * (CLAUDE.md §5.3).
 */

import { useCallback, useEffect, useRef, useSyncExternalStore } from "react";
import { useMsal } from "@azure/msal-react";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { ApiError } from "@/shared/api/apiFetch";
import { type Me, getMe } from "@/shared/api/me";

interface MeState {
  data: Me | null;
  loading: boolean;
  error: string | null;
  /** The API error `code` (e.g. `account_not_provisioned`) when the failure
   *  came from the backend envelope, so callers can branch on it. */
  errorCode: string | null;
}

/** Older than this, a newly mounted caller refreshes the answer. */
const STALE_MS = 60_000;

const LOADING: MeState = { data: null, loading: true, error: null, errorCode: null };
const SIGNED_OUT: MeState = { data: null, loading: false, error: null, errorCode: null };

interface Store {
  /** The account the answer belongs to ("" = nobody signed in). */
  key: string;
  state: MeState;
  fetchedAt: number;
  inFlight: Promise<void> | null;
}

let store: Store = { key: "", state: SIGNED_OUT, fetchedAt: 0, inFlight: null };
const listeners = new Set<() => void>();

function set(next: Partial<Store>): void {
  store = { ...store, ...next };
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function snapshot(): Store {
  return store;
}

/** Fetch for `key`, sharing a request already in flight. `quiet` keeps the
 *  current answer on screen while it runs (a background refresh). */
function load(key: string, getToken: () => Promise<string>, quiet: boolean): Promise<void> {
  if (store.key === key && store.inFlight) return store.inFlight;
  const run = (async () => {
    try {
      const me = await getMe(await getToken());
      if (store.key === key) {
        set({
          state: { data: me, loading: false, error: null, errorCode: null },
          fetchedAt: Date.now(),
          inFlight: null,
        });
      }
    } catch (err) {
      if (store.key === key) {
        set({
          state: {
            data: null,
            loading: false,
            error: err instanceof Error ? err.message : String(err),
            errorCode: err instanceof ApiError ? err.code : null,
          },
          fetchedAt: Date.now(),
          inFlight: null,
        });
      }
    }
  })();
  store = { ...store, inFlight: run };
  if (!quiet) set({ state: { ...store.state, loading: true, error: null, errorCode: null } });
  return run;
}

/** Forget the shared answer — for tests, which sign different people in. */
export function resetMeCache(): void {
  store = { key: "", state: SIGNED_OUT, fetchedAt: 0, inFlight: null };
  listeners.clear();
}

export function useMe(): MeState & {
  reload: () => void;
  /** Replace the shared profile without a refetch. */
  update: (next: Me) => void;
} {
  const { accounts } = useMsal();
  // Keyed on who is signed in, not on the array's identity: a provider (or a
  // test double) that hands back a fresh array each render must not refetch.
  const accountKey = accounts.map((a) => a.homeAccountId).join("|");
  const getToken = useAccessToken();
  // Read through a ref: `useAccessToken()` returns a new function on most
  // renders, and an effect that listed it would refetch forever.
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;
  const current = useSyncExternalStore(subscribe, snapshot, snapshot);

  useEffect(() => {
    const token = () => getTokenRef.current();
    if (!accountKey) {
      if (store.key !== "") set({ key: "", state: SIGNED_OUT, fetchedAt: 0, inFlight: null });
      return;
    }
    if (store.key !== accountKey) {
      // Another person (or the first sign-in): never show the previous answer.
      set({ key: accountKey, state: LOADING, fetchedAt: 0, inFlight: null });
      void load(accountKey, token, false);
      return;
    }
    const stale = Date.now() - store.fetchedAt > STALE_MS;
    if (!store.inFlight && (stale || store.state.error !== null)) {
      void load(accountKey, token, store.state.data !== null);
    }
  }, [accountKey]);

  const reload = useCallback(() => {
    if (accountKey) void load(accountKey, () => getTokenRef.current(), false);
  }, [accountKey]);
  const update = useCallback((next: Me) => {
    set({ state: { ...store.state, data: next } });
  }, []);

  const state = current.key === accountKey ? current.state : accountKey ? LOADING : SIGNED_OUT;
  return { ...state, reload, update };
}
